import { useViewerTimezone } from "@/hooks/useViewerTimezone";
import { useNavigate } from "react-router-dom";
import { LogOut, MessageSquareHeart, Clock, Shield, Monitor } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { useUserRole } from "@/hooks/useUserRole";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ThemeToggle } from "./ThemeToggle";

const ROLE_LABELS: Record<string, string> = {
  admin: "Administrator",
  analyst: "Observer",
  dispatcher: "Dispatcher",
  driver: "Driver",
  service_technician: "Service Technician",
  terminal_staff: "Terminal Staff",
};

function getInitials(user: { email?: string; user_metadata?: Record<string, any> } | null): string {
  const fullName = user?.user_metadata?.full_name || user?.user_metadata?.name;
  if (fullName && typeof fullName === "string") {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  }
  const email = user?.email || "";
  return email.slice(0, 2).toUpperCase();
}

function getDisplayName(user: { email?: string; user_metadata?: Record<string, any> } | null): string {
  return user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || "User";
}

/** Saved order times are shown in this zone (docs/TIMEZONES.md). */
function getTimeZoneLabel(tz: string): string {
  try {
    const long = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "long" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
    return long || tz;
  } catch {
    return "Unknown";
  }
}

export function UserProfilePopover({ compact = false, asSheet = false }: { compact?: boolean; asSheet?: boolean }) {
  const navigate = useNavigate();
  const viewerTz = useViewerTimezone();
  const { user } = useAuth();
  const { activeTenant } = useTenant();
  const { roles } = useUserRole();
  // Every role label, joined, for the feedback form.
  const feedbackRole = roles.length > 0
    ? roles.map((r) => ROLE_LABELS[r] || r).join(", ")
    : "Unknown";

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const initials = getInitials(user);
  const displayName = getDisplayName(user);
  const email = user?.email || "";
  const timeZone = getTimeZoneLabel(viewerTz);
  const envLabel = activeTenant
    ? activeTenant.charAt(0).toUpperCase() + activeTenant.slice(1)
    : "Production";
  const roleLabel = roles.length > 0
    ? roles.map((r) => ROLE_LABELS[r] || r).join(", ")
    : "No role assigned";

  const trigger = (
    <button className={`flex items-center gap-3 rounded-md p-2 hover:bg-sidebar-accent transition-colors text-left ${compact ? "" : "w-full"}`}>
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarFallback className="text-sm font-medium bg-primary text-primary-foreground">
          {initials}
        </AvatarFallback>
      </Avatar>
      {!compact && (
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-sidebar-foreground truncate">{displayName}</p>
          <p className="text-xs text-sidebar-foreground/60 truncate">{email}</p>
        </div>
      )}
    </button>
  );

  const body = (
    <>
        <div className="p-4 pb-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{email}</p>
            </div>
          </div>
        </div>

        <Separator />

        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Time Zone</p>
              <p className="text-sm text-foreground truncate">{timeZone}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Monitor className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Environment</p>
              <p className="text-sm text-foreground">{envLabel}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Shield className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Role Access</p>
              <p className="text-sm text-foreground">{roleLabel}</p>
            </div>
          </div>
        </div>

        <Separator />

        <div className="px-4 py-3">
          <p className="text-xs text-muted-foreground mb-2">Theme</p>
          <ThemeToggle />
        </div>

        <Separator />

        <div className="p-2">
          <a
            href={`https://script.google.com/a/macros/aurora.tech/s/AKfycbw51r6TuQdmMoTbJ-jNXu1b5bHKb6fTCjQNR-kyJUrF7cZUN4y6W5fAoQSqbEzBegh7/exec?source=ATMS&role=${encodeURIComponent(feedbackRole)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 w-full rounded-md px-2 py-2 text-sm text-foreground hover:bg-accent transition-colors"
          >
            <MessageSquareHeart className="h-4 w-4 text-muted-foreground" />
            Help us improve
          </a>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-3 w-full rounded-md px-2 py-2 text-sm text-foreground hover:bg-accent transition-colors"
          >
            <LogOut className="h-4 w-4 text-muted-foreground" />
            Sign out
          </button>
        </div>
    </>
  );

  if (asSheet) {
    return (
      <Sheet>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="bottom" className="p-0 rounded-t-xl max-h-[85vh] overflow-y-auto">
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent side={compact ? "bottom" : "top"} align={compact ? "end" : "start"} className="w-72 p-0 ml-4">
        {body}
      </PopoverContent>
    </Popover>
  );
}
